import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell as TableCellOrig,
  TableHead as TableHeadOrig,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { tableHeadClass, tableCellClass, tableCellLastRowClass } from "@/components/ui/table-styles";
import PaginationButton from "@/components/ui/pagination-button";
import { PaymentMethod, PaymentType } from "@/gen/purchase_invoice/common_pb";
import { convertToCurrencyRounded } from "@/utils/currency_converter";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import {
  DoubleArrowLeftIcon,
  DoubleArrowRightIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@radix-ui/react-icons";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { Route } from "..";
import { useNavigate, useRouter } from "@tanstack/react-router";
import { Checkbox } from "@/components/ui/checkbox";
import DialogPayment from "./dialog";
dayjs.extend(customParseFormat);

const TableCell = ({ className = "", ...props }) => (
  <TableCellOrig className={[tableCellClass, className].join(" ")} {...props} />
);
const TableHead = ({ className = "", ...props }) => (
  <TableHeadOrig className={[tableHeadClass, className].join(" ")} {...props} />
);

const Screen = () => {
    const data = Route.useLoaderData()
    const search = Route.useSearch()
    const router = useRouter()

    const [selectedInvoice, setSelectedInvoice] = useState<string[] | null>(null)

    return (
        <div>
        <div className="flex items-center justify-between">
            <Breadcrumb>
                <BreadcrumbList className="flex items-center">
                    <BreadcrumbItem>
                        <BreadcrumbLink onClick={() => {
                            router.navigate({
                                to: '/suppliers',
                            })
                        }} style={{
                            cursor: 'pointer'
                        }}>Suppliers</BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>
                        <BreadcrumbLink onClick={() => {
                            router.navigate({
                                to: '/suppliers/$supplierId',
                                params: {
                                    supplierId: data.supplier.supplier?.id || "",
                                },
                            })
                        }} style={{
                            cursor: 'pointer'
                        }}>
                        {data.supplier.supplier?.name}
                        </BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>
                        <BreadcrumbPage>Make Payment</BreadcrumbPage>
                    </BreadcrumbItem>
                </BreadcrumbList>
            </Breadcrumb>

            {selectedInvoice && selectedInvoice.length > 0 && (
                <DialogPayment selectedInvoices={selectedInvoice} invoiceRawData={data.invoices} supplierID={data.supplier.supplier?.id!}/>
            )}
        </div>
        <hr className="my-4" />
        <div className="border rounded-lg bg-white overflow-hidden">
            <Table className="text-sm">
            <TableHeader>
                <TableRow className="">
                    <TableHead>
                        <Checkbox 
                            className=""
                            checked={selectedInvoice?.length === data.invoices.invoices.length}
                            onCheckedChange={() => setSelectedInvoice(selectedInvoice?.length === data.invoices.invoices.length ? null : data.invoices.invoices.map(item => item.id))}
                        />
                    </TableHead>
                    <TableHead>GRN Number</TableHead>
                    <TableHead>Invoice No.</TableHead>
                    <TableHead>Invoice Date</TableHead>
                    <TableHead>Supplier Name</TableHead>
                    <TableHead>Created Date</TableHead>
                    <TableHead>Payment Mode</TableHead>
                    <TableHead>Payment Type</TableHead>
                    <TableHead>Total Amount</TableHead>
                    <TableHead>Due Date</TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
                {data.invoices.invoices.map((item, index) => (
                <TableRow key={item.id} className="font-medium">
                    <TableCell>
                        <Checkbox 
                            checked={selectedInvoice?.includes(item.id)}
                            onCheckedChange={(checked) => {
                                if (checked) {
                                    setSelectedInvoice([...(selectedInvoice || []), item.id])
                                } else {
                                    setSelectedInvoice(selectedInvoice?.filter(id => id !== item.id) || [])
                                }
                            }}
                        />
                    </TableCell>
                    <TableCell className={index === data.invoices.invoices.length - 1 ? tableCellLastRowClass : ""}>{item.grnNumber}</TableCell>
                    <TableCell className={index === data.invoices.invoices.length - 1 ? tableCellLastRowClass : ""}>{item.invoiceNumber}</TableCell>
                    <TableCell className={index === data.invoices.invoices.length - 1 ? tableCellLastRowClass : ""}>{dayjs(item.invoiceDate?.toDate()).format("DD/MM/YYYY")}</TableCell>
                    <TableCell className={index === data.invoices.invoices.length - 1 ? tableCellLastRowClass : ""}>{item.supplierName}</TableCell>
                    <TableCell className={index === data.invoices.invoices.length - 1 ? tableCellLastRowClass : ""}>{dayjs(item.createdAt?.toDate()).format("DD/MM/YYYY")}</TableCell>
                    <TableCell className={index === data.invoices.invoices.length - 1 ? tableCellLastRowClass : ""}>{
                    Object.keys(PaymentMethod).find(
                        (key) => PaymentMethod[key as keyof typeof PaymentMethod] === item.paymentMethod
                    ) || "Unknown"
                    }</TableCell>
                    <TableCell className={index === data.invoices.invoices.length - 1 ? tableCellLastRowClass : ""}>{
                    Object.keys(PaymentType).find(
                        (key) => PaymentType[key as keyof typeof PaymentType] === item.paymentType
                    ) || "Unknown"
                    }</TableCell>
                    <TableCell className={index === data.invoices.invoices.length - 1 ? tableCellLastRowClass : ""}>
                    {convertToCurrencyRounded(item.totalAmount)}
                    </TableCell>
                    <TableCell className={index === data.invoices.invoices.length - 1 ? tableCellLastRowClass : ""}>
                    {item.dueDate ? dayjs(item.dueDate.toDate()).format("DD/MM/YYYY") : "N/A"}
                    </TableCell>
                </TableRow>
                ))}
            </TableBody>
            </Table>
            <div className="flex items-center justify-between py-4 px-6 text-sm border-t bg-white">
                <p>{data.invoices.totalCount} <span className="text-gray-500 font-light">count</span></p>
                <div className="flex items-center gap-1">
                    <PaginationButton disabled onClick={() => {
                        router.navigate({
                            search: {
                                ...search,
                                page: 1,
                            },
                        });
                    }}>
                        <DoubleArrowLeftIcon className="h-4 w-4" />
                    </PaginationButton>
                    <PaginationButton disabled onClick={() => {
                        router.navigate({
                            search: {
                                ...search,
                                page: Math.max((search.page ? search.page : 1) - 1, 1),
                            },
                        });
                    }}>
                        <ChevronLeftIcon className="h-4 w-4" />
                    </PaginationButton>
                    {/* <PaginationButton active onClick={() => {}}>

                    </PaginationButton> */}
                    {Array.from({length: Math.ceil(data.invoices.totalCount/search.pageSize)}).map((_, index) => (
                        <PaginationButton
                            key={index}
                            active={search.page ? search.page === index + 1 : index === 0}
                            onClick={() => {
                                router.navigate({
                                    search: {
                                        ...search,
                                        page: index + 1,
                                    },
                                });
                            }}
                        >
                            {index + 1}
                        </PaginationButton>
                    ))}
                    <PaginationButton disabled onClick={() => {
                        router.navigate({
                            search: {
                                ...search,
                                page: Math.min((search.page ? search.page : 1) + 1, Math.ceil(data.invoices.totalCount/search.pageSize)),
                            },
                        });
                    }}>
                        <ChevronRightIcon className="h-4 w-4" />
                    </PaginationButton>
                    <PaginationButton disabled onClick={() => {
                        router.navigate({
                            search: {
                                ...search,
                                page: Math.ceil(data.invoices.totalCount/search.pageSize),
                            },
                        });
                    }}>
                        <DoubleArrowRightIcon className="h-4 w-4" />
                    </PaginationButton>
                </div>
            </div>
        </div>
        </div>
    );
};

export default Screen;